import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import {emitEventLog} from '@di-shared/features/analytics';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useBrakePadSeverity, {BrakeSevereLevel} from '@di-shared/features/diag/brake/useBrakePadSeverity';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export enum BrakeStatusType {
  GOOD,
  SEVERE,
}

function BrakePadStatusCheck(severeLevel: string) {
  switch (severeLevel) {
    case BrakeSevereLevel.NORMAL:
    case BrakeSevereLevel.CAUTION:
      return BrakeStatusType.GOOD;
    case BrakeSevereLevel.SEVERE:
      return BrakeStatusType.SEVERE;
    default:
      throw new ExpectedError(`BrakePadStatusCheck Error: ${severeLevel}`);
  }
}

export interface VehicleBrakePadKrProps {
  vin: string;
}

function VehicleBrakePadKr({vin}: VehicleBrakePadKrProps) {
  const {t} = useInferableTranslation();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.brakePad.getBrake1), vin],
    queryFn: () => diApi.brakePad.getBrake1({vin}),
    select: ({data}) => data,
  });

  const {getSevereLevel} = useBrakePadSeverity({vin});
  const severeLevel = getSevereLevel(data.severeTripCount, data.avgSevereCount);
  const brakePadStatusCheck = BrakePadStatusCheck(severeLevel);

  return (
    <li className="car_diagnosis_item">
      <LinkTo
        className="car_diagnosis_link"
        to={ROUTE_PATHS.DIAG_BRAKE.getFullPath(vin)}
        onClickBypass={() =>
          emitEventLog({
            event: 'vehicle_status_check',
            interaction_type: 'view brake pad',
            click_text: t('T30054'),
            click_location: 'body',
          })
        }
      >
        <div className="section_container">
          <strong className="car_diagnosis_title">{t('T30054')}</strong>
          <div className="badge_box">
            <span
              className={clsx({
                'badge badge_basic_container badge_20px': brakePadStatusCheck === BrakeStatusType.GOOD,
                'badge badge_container_red badge_20px': brakePadStatusCheck === BrakeStatusType.SEVERE,
              })}
            >
              {brakePadStatusCheck === BrakeStatusType.GOOD && t('T20079')}
              {brakePadStatusCheck === BrakeStatusType.SEVERE && t('T20086')}
            </span>
          </div>
          <span
            className={clsx('ic', {
              ic_brakepad_good: brakePadStatusCheck === BrakeStatusType.GOOD,
              ic_brakepad_warning: brakePadStatusCheck === BrakeStatusType.SEVERE,
            })}
          />
        </div>
      </LinkTo>
    </li>
  );
}

VehicleBrakePadKr.Error = VehicleBrakePadError;
export default VehicleBrakePadKr;

function VehicleBrakePadError() {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();

  return (
    <li className="car_diagnosis_item">
      <div
        className="car_diagnosis_link"
        role={'button'}
        onClick={() =>
          openAlert({
            title: t('T30054'),
            message: t('T30358') + '\n' + t('T10160_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
          })
        }
      >
        <div className="section_container">
          <strong className="car_diagnosis_title">{t('T30054')}</strong>
          <span className="ic ic_brakepad_nodata" />
        </div>
      </div>
    </li>
  );
}
